import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, MessageCircleIcon, MapPinIcon, PhoneIcon, MailIcon, ChevronRightIcon } from 'lucide-react';
export function Layout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  // Check if current page has a dark hero (Home, Conveyancing, etc.)
  // For simplicity, we'll assume all pages have a hero section that requires a transparent nav initially,
  // except maybe Contact/FAQs. Let's just make it transparent on top for all pages and solid on scroll.
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  // The transparent-over-hero nav only makes sense on the Home page, which has a
  // full-bleed dark hero. Every other page opens on a light background, so the nav
  // must be solid (white background, dark text) from the top to stay legible.
  const isHome = location.pathname === '/';
  const solid = isScrolled || !isHome;
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'About Us',
    path: '/about'
  }, {
    name: 'Practice Areas',
    path: '/practice-areas'
  }, {
    name: 'Conveyancing',
    path: '/conveyancing'
  }, {
    name: 'Our Team',
    path: '/team'
  }, {
    name: 'Legal Insights',
    path: '/insights'
  }, {
    name: 'FAQs',
    path: '/faqs'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <div className="min-h-screen flex flex-col font-sans text-brand-charcoal bg-brand-offwhite" data-id="element-24">
      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${solid ? 'bg-white shadow-soft py-4' : 'bg-transparent py-6'}`} data-id="element-25">
        <div className="max-w-container mx-auto px-6 flex items-center justify-between" data-id="element-26">
          <Link to="/" className="z-50 flex items-center" aria-label="McErick Attorneys — Home" data-id="element-27">
            <span className={`flex items-center justify-center rounded-xl bg-white transition-all duration-300 ${solid ? 'p-1.5 shadow-sm' : 'p-2 shadow-md'}`} data-id="element-28">
              <img src="https://cdn.magicpatterns.com/uploads/mfsSfzoXkWttHmpz3JuHjm/pasted-image.jpg" alt="McErick Attorneys" className={`w-auto object-contain transition-all duration-300 ${solid ? 'h-12' : 'h-14'}`} data-id="element-29" />
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-8" data-id="element-30">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`text-sm font-medium transition-colors hover:text-brand-gold ${solid ? 'text-brand-charcoal' : 'text-white drop-shadow-md'}`} data-id="element-31">
                {link.name}
              </Link>)}
            <Link to="/contact" className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${solid ? 'bg-brand-green text-white hover:bg-brand-green-dark' : 'bg-brand-gold text-white hover:bg-brand-gold-light shadow-lg'}`} data-id="element-32">
              Book Consultation
            </Link>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden z-50 p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-33">
            {mobileMenuOpen ? <XIcon className={solid || mobileMenuOpen ? 'text-brand-charcoal' : 'text-white'} data-id="element-34" /> : <MenuIcon className={solid ? 'text-brand-charcoal' : 'text-white'} data-id="element-35" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-36">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed inset-0 z-40 bg-white pt-24 px-6 pb-6 flex flex-col overflow-y-auto lg:hidden" data-id="element-37">
            <div className="flex flex-col space-y-6" data-id="element-38">
              {navLinks.map(link => <Link key={link.name} to={link.path} className="text-2xl font-serif text-brand-charcoal border-b border-brand-grey pb-4" data-id="element-39">
                  {link.name}
                </Link>)}
              <Link to="/contact" className="mt-4 bg-brand-green text-white text-center py-4 rounded-xl font-medium text-lg" data-id="element-40">
                Book Consultation
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-grow" data-id="element-41">
        <Outlet data-id="element-42" />
      </main>

      {/* Footer */}
      <footer className="bg-brand-charcoal text-brand-offwhite pt-20 pb-10" data-id="element-43">
        <div className="max-w-container mx-auto px-6" data-id="element-44">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16" data-id="element-45">
            <div data-id="element-46">
              <Link to="/" className="inline-flex mb-6" aria-label="McErick Attorneys — Home" data-id="element-47">
                <span className="inline-flex items-center justify-center rounded-xl bg-white p-3 shadow-md" data-id="element-48">
                  <img src="https://cdn.magicpatterns.com/uploads/mfsSfzoXkWttHmpz3JuHjm/pasted-image.jpg" alt="McErick Attorneys" className="h-20 w-auto object-contain" data-id="element-49" />
                </span>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-6" data-id="element-50">
                Providing professional legal services to individuals, families,
                entrepreneurs and businesses throughout Botswana with integrity
                and efficiency.
              </p>
            </div>

            <div data-id="element-51">
              <h4 className="font-serif text-xl font-semibold mb-6 text-white" data-id="element-52">
                Quick Links
              </h4>
              <ul className="space-y-3" data-id="element-53">
                {navLinks.slice(0, 5).map(link => <li key={link.name} data-id="element-54">
                    <Link to={link.path} className="text-gray-400 hover:text-brand-gold transition-colors text-sm flex items-center" data-id="element-55">
                      <ChevronRightIcon className="w-4 h-4 mr-2 text-brand-gold" data-id="element-56" />
                      {link.name}
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-57">
              <h4 className="font-serif text-xl font-semibold mb-6 text-white" data-id="element-58">
                Practice Areas
              </h4>
              <ul className="space-y-3" data-id="element-59">
                {['Conveyancing', 'Family Law', 'Commercial Law', 'Labour Law', 'Civil Litigation'].map(area => <li key={area} data-id="element-60">
                    <Link to="/practice-areas" className="text-gray-400 hover:text-brand-gold transition-colors text-sm flex items-center" data-id="element-61">
                      <ChevronRightIcon className="w-4 h-4 mr-2 text-brand-gold" data-id="element-62" />
                      {area}
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-63">
              <h4 className="font-serif text-xl font-semibold mb-6 text-white" data-id="element-64">
                Contact Us
              </h4>
              <ul className="space-y-4" data-id="element-65">
                <li className="flex items-start text-sm text-gray-400" data-id="element-66">
                  <MapPinIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0 mt-0.5" data-id="element-67" />
                  <span data-id="element-68">
                    Plot 70667, 7th Floor, The Tower
                    <br data-id="element-69" />
                    Farscape Precinct, Fairgrounds
                    <br data-id="element-70" />
                    Gaborone, Botswana
                  </span>
                </li>
                <li className="flex items-center text-sm text-gray-400" data-id="element-71">
                  <PhoneIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0" data-id="element-72" />
                  <span data-id="element-73">(+267) 316 5472</span>
                </li>
                <li className="flex items-center text-sm text-gray-400" data-id="element-74">
                  <MailIcon className="w-5 h-5 mr-3 text-brand-gold shrink-0" data-id="element-75" />
                  <span data-id="element-76">charles@mcerickattorneys.co.bw</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-gray-500" data-id="element-77">
            <p data-id="element-78">
              &copy; {new Date().getFullYear()} McErick Attorneys. All rights
              reserved.
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0" data-id="element-79">
              <Link to="/privacy" className="hover:text-white transition-colors" data-id="element-80">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors" data-id="element-81">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/26772553210" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 bg-[#25D366] text-white p-4 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all z-40 flex items-center justify-center" aria-label="Chat on WhatsApp" data-id="element-82">
        <MessageCircleIcon className="w-6 h-6" data-id="element-83" />
      </a>
    </div>;
}